/**
 * Workshop version of EmailFrame.
 */
import { Icons } from "@/components/ui/icons";
import { Button } from "@/components/ui";
import { cn } from "@/lib/cn";
import { FrameHeader } from "@/workshop/components/shapes/shared/frame-header";

interface EmailFrameProps {
  to?: string;
  subject?: string;
  body?: string;
  title?: string;
  agentName?: string;
  onSend?: () => void;
  onEdit?: () => void;
  onResizeStart?: React.MouseEventHandler;
  className?: string;
  "data-testid"?: string;
}

export function EmailFrame({
  to = "sarah@company.com",
  subject = "Q3 Review Follow-up",
  body = "Hi Sarah,\n\nThank you for your time in the Q3 review meeting. I wanted to follow up on the action items we discussed...",
  title = "Follow-up Email",
  agentName = "Writer",
  onSend,
  onEdit,
  onResizeStart: _onResizeStart,
  className,
  "data-testid": testId = "email-frame",
}: EmailFrameProps) {
  return (
    <div className={cn("denker-glass-frame flex flex-col rounded-frame overflow-hidden", className)} data-testid={testId}>
      <FrameHeader title={title} icon={Icons.Send} accentColor="bg-frame-email" agentName={agentName} data-testid={`${testId}-header`} />
      <div className="flex flex-1 flex-col gap-1.5 p-2 overflow-hidden">
        <div className="flex flex-col gap-0.5 rounded border border-border p-1.5" data-testid={`${testId}-meta`}>
          <div className="flex items-center gap-1.5">
            <span className="shrink-0 text-canvas-2xs text-muted">To:</span>
            <span className="truncate text-canvas-sm text-secondary">{to}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="shrink-0 text-canvas-2xs text-muted">Subject:</span>
            <span className="truncate text-canvas-sm font-medium text-secondary">{subject}</span>
          </div>
        </div>
        <div className="flex flex-1 flex-col rounded border border-border p-1.5 overflow-hidden" data-testid={`${testId}-body`}>
          <p className="whitespace-pre-wrap text-canvas-sm leading-[1.4] text-primary line-clamp-6">{body}</p>
        </div>
      </div>
      <div className="flex h-10 w-full items-center gap-1.5 border-t border-glass-stroke-faint px-2">
        <Button
          type="button"
          onClick={onSend}
          size="sm"
          className="bg-frame-email text-white shadow-none hover:brightness-110"
          data-testid={`${testId}-send-btn`}
        >
          Send
        </Button>
        <Button
          type="button"
          onClick={onEdit}
          variant="secondary"
          size="sm"
          data-testid={`${testId}-edit-btn`}
        >
          Edit
        </Button>
      </div>
    </div>
  );
}

export type { EmailFrameProps };
